import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Plus, Heart, Trash2, X, Newspaper, Tag } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function NewsfeedPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ title: '', content: '', tags: '' });

  useEffect(() => {
    axios.get(`${API}/api/news`)
      .then(r => setPosts(Array.isArray(r.data) ? r.data : []))
      .catch(() => { setPosts([]); })
      .finally(() => setLoading(false));
  }, []);

  const handleLike = async (id) => {
    try {
      const { data } = await axios.post(`${API}/api/news/${id}/like`, {});
      setPosts(prev => prev.map(p => p.id === id ? { ...p, liked_by_me: data.liked, like_count: data.like_count } : p));
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Beitrag löschen?')) return;
    try {
      await axios.delete(`${API}/api/news/${id}`);
      setPosts(prev => prev.filter(p => p.id !== id));
    } catch {}
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) { setError('Titel und Inhalt erforderlich'); return; }
    setSaving(true);
    setError('');
    try {
      const tags = form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
      const { data } = await axios.post(`${API}/api/news`, { title: form.title, content: form.content, tags });
      setPosts(prev => [data, ...prev]);
      setShowModal(false);
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    setSaving(false);
  };

  return (
    <div className="p-4 md:p-6 space-y-4 animate-fade-in" data-testid="newsfeed-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-2xl font-bold text-emerald-50">Newsfeed</h1>
          <p className="text-emerald-100/50 text-sm mt-0.5">Community-Beiträge und Neuigkeiten</p>
        </div>
        <button onClick={() => { setForm({ title: '', content: '', tags: '' }); setError(''); setShowModal(true); }}
          className="btn-primary flex items-center gap-2" data-testid="create-news-btn">
          <Plus size={16} /> Beitrag
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16">
          <Newspaper size={48} className="mx-auto text-emerald-500/30 mb-4" />
          <p className="text-emerald-100/50">Noch keine Beiträge</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="news-list">
          {posts.map(post => (
            <div key={post.id} className="card-grow p-5" data-testid={`news-post-${post.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-semibold text-emerald-100 text-lg">{post.title}</h3>
                  <p className="text-xs text-emerald-100/40 mt-1">
                    {post.author_name} · {new Date(post.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </p>
                </div>
                {(user?.id === post.author_id || user?.role === 'admin') && (
                  <button onClick={() => handleDelete(post.id)} className="p-1.5 rounded text-emerald-100/30 hover:text-red-400 transition-colors flex-shrink-0" data-testid={`delete-news-${post.id}`}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>

              <p className="text-sm text-emerald-100/70 mt-3 leading-relaxed">{post.content}</p>

              {post.tags?.length > 0 && (
                <div className="flex gap-1 flex-wrap mt-3">
                  {post.tags.map(tag => (
                    <span key={tag} className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full text-emerald-100/50"
                      style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid #1B2A21' }}>
                      <Tag size={9} /> {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-4 mt-4 pt-3" style={{ borderTop: '1px solid #1B2A21' }}>
                <button onClick={() => handleLike(post.id)}
                  className={`flex items-center gap-1.5 text-sm transition-colors ${post.liked_by_me ? 'text-emerald-400' : 'text-emerald-100/40 hover:text-emerald-400'}`}
                  data-testid={`like-btn-${post.id}`}>
                  <Heart size={16} fill={post.liked_by_me ? 'currentColor' : 'none'} />
                  <span>{post.like_count || 0}</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-backdrop" data-testid="news-modal">
          <div className="modal-content p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-emerald-50">Neuer Beitrag</h2>
              <button onClick={() => setShowModal(false)} className="text-emerald-100/40 hover:text-emerald-100"><X size={20} /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-3">
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Titel *</label>
                <input className="input-grow" placeholder="Beitragstitel" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="news-title-input" />
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Inhalt *</label>
                <textarea className="input-grow" rows={5} placeholder="Was möchtest du teilen?" value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} data-testid="news-content-input" />
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Tags (kommagetrennt)</label>
                <input className="input-grow" placeholder="z.B. tip, grow, indoor" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} data-testid="news-tags-input" />
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Abbrechen</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="save-news-btn">
                  {saving ? 'Posten...' : 'Veröffentlichen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
